/**
 * 初期データ移行用CSVインポート(kickoff 1-6)。
 * オペレーター用・会社用の2テンプレート。現行シートの OP管理・会社情報から
 * 転記したCSVを取り込む。テンプレートは docs/templates/ にも同梱している。
 */
import { useMemo, useState, type ChangeEvent } from 'react';
import { useCurrentUser } from '@core/auth/AuthProvider';
import { SortTh, sortRows, useSort } from '@core/components/tableTools';
import { useToast } from '@core/components/Toast';
import { parseCsv, readCsvFile } from '@core/lib/csv';
import { toDateLabel } from '@core/lib/date';
import { formatYen } from '@core/lib/money';
import {
  BUSINESS_LABELS,
  OPERATOR_STATUSES,
  OPERATOR_STATUS_LABELS,
  PAY_TYPE_LABELS,
  WORK_STYLE_LABELS,
  type Company,
  type Operator,
} from '@core/lib/types';
import { fetchOperators } from './operatorRepo';
import { fetchCompanies } from './companyRepo';
import BlueBeanUploadCard from '@core/import/BlueBeanUploadCard';
import {
  COMPANY_TEMPLATE_CSV,
  OPERATOR_TEMPLATE_CSV,
  downloadCsv,
  planCompanyImport,
  planOperatorImport,
  runCompanyImport,
  runOperatorImport,
  type CompanyImportPlan,
  type OperatorImportPlan,
} from './csvImport';

type Tab = 'operators' | 'companies' | 'bluebean';

type LoadedFile = { name: string; text: string; rowCount: number };

/** 取込プレビュー表の並べ替えキー(オペレーター / 会社) */
type OpSortKey = 'name' | 'bb_id' | 'company' | 'business' | 'status' | 'work_style' | 'pay_type' | 'wage' | 'joined_at';
type CoSortKey = 'name' | 'unit_price' | 'product_type' | 'delivery_timing' | 'codes' | 'phone';

const companyNameOf = (companies: Company[], id: string | null) => (id ? (companies.find((c) => c.id === id)?.name ?? id) : '—');

export default function CsvImportPage() {
  const user = useCurrentUser();
  const toast = useToast();
  const [tab, setTab] = useState<Tab>('operators');

  const [opFile, setOpFile] = useState<LoadedFile | null>(null);
  const [opPlan, setOpPlan] = useState<OperatorImportPlan | null>(null);
  const [opExisting, setOpExisting] = useState<Operator[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [coFile, setCoFile] = useState<LoadedFile | null>(null);
  const [coPlan, setCoPlan] = useState<CompanyImportPlan | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState('');
  const { sort: opSort, toggle: toggleOpSort } = useSort<OpSortKey>();
  const { sort: coSort, toggle: toggleCoSort } = useSort<CoSortKey>();

  const companyName = (id: string | null) => companyNameOf(companies, id);

  const opCreates = useMemo(() => {
    const creates = opPlan?.creates ?? [];
    return sortRows(creates, opSort, (c, key) => {
      switch (key) {
        case 'name':
          return c.name;
        case 'bb_id':
          return c.accounts[0]?.bb_id || null;
        case 'company':
          return Array.from(new Set(c.accounts.map((a) => companyNameOf(companies, a.company_id)))).join('、');
        case 'business':
          return Array.from(new Set(c.accounts.map((a) => BUSINESS_LABELS[a.business]))).join('／');
        case 'status':
          return OPERATOR_STATUSES.indexOf(c.status);
        case 'work_style':
          return WORK_STYLE_LABELS[c.work_style];
        case 'pay_type':
          return PAY_TYPE_LABELS[c.pay_type];
        case 'wage':
          return c.accounts.length > 0 ? Math.max(...c.accounts.map((a) => a.current_wage)) : null;
        case 'joined_at':
          return c.joined_at;
      }
    });
  }, [opPlan, companies, opSort]);

  const coCreates = useMemo(() => {
    const creates = coPlan?.creates ?? [];
    return sortRows(creates, coSort, (c, key) => {
      switch (key) {
        case 'name':
          return c.name;
        case 'unit_price':
          return c.unit_price;
        case 'product_type':
          return BUSINESS_LABELS[c.product_type];
        case 'delivery_timing':
          return c.delivery_timing || null;
        case 'codes':
          return c.bb_business_codes.length > 0 ? c.bb_business_codes.join(', ') : null;
        case 'phone':
          return c.phone || null;
      }
    });
  }, [coPlan, coSort]);

  async function handleFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ''; // 同じファイルの選び直しを許可
    if (!file) return;
    setDone('');
    setBusy(true);
    try {
      const text = await readCsvFile(file);
      const table = parseCsv(text);
      const loaded = { name: file.name, text, rowCount: table.rows.length };
      if (tab === 'operators') {
        const [existing, cos] = await Promise.all([fetchOperators(), fetchCompanies()]);
        setOpExisting(existing);
        setCompanies(cos);
        setOpFile(loaded);
        setOpPlan(planOperatorImport(table, existing, cos));
      } else {
        const existing = await fetchCompanies();
        setCoFile(loaded);
        setCoPlan(planCompanyImport(table, existing));
      }
    } catch (err) {
      toast(err instanceof Error ? err.message : 'ファイルを読み込めませんでした');
    } finally {
      setBusy(false);
    }
  }

  async function handleImport() {
    setBusy(true);
    try {
      if (tab === 'operators' && opPlan && opFile) {
        const r = await runOperatorImport(
          opPlan,
          { fileName: opFile.name, fileText: opFile.text, rowCount: opFile.rowCount, existing: opExisting },
          user.id,
        );
        setDone(`取込が完了しました: 新規 ${r.imported}名 / BB ID追記 ${r.merged}件`);
        setOpFile(null);
        setOpPlan(null);
      } else if (tab === 'companies' && coPlan && coFile) {
        const r = await runCompanyImport(coPlan, { fileName: coFile.name, fileText: coFile.text, rowCount: coFile.rowCount }, user.id);
        setDone(`取込が完了しました: 新規 ${r.imported}社`);
        setCoFile(null);
        setCoPlan(null);
      }
    } catch (err) {
      toast(err instanceof Error ? err.message : '取込に失敗しました');
    } finally {
      setBusy(false);
    }
  }

  const plan = tab === 'operators' ? opPlan : coPlan;
  const file = tab === 'operators' ? opFile : coFile;
  const createCount = tab === 'operators' ? (opPlan?.creates.length ?? 0) : (coPlan?.creates.length ?? 0);
  const mergeCount = tab === 'operators' ? (opPlan?.merges.length ?? 0) : 0;
  const canRun = plan !== null && createCount + mergeCount > 0;

  return (
    <div className="screen master-screen" style={{ maxWidth: 1280 }}>
      <div className="tabs">
        <button className={`tab${tab === 'operators' ? ' on' : ''}`} onClick={() => { setTab('operators'); setDone(''); }}>
          オペレーター
        </button>
        <button className={`tab${tab === 'companies' ? ' on' : ''}`} onClick={() => { setTab('companies'); setDone(''); }}>
          会社
        </button>
        <button className={`tab${tab === 'bluebean' ? ' on' : ''}`} onClick={() => { setTab('bluebean'); setDone(''); }}>
          BlueBean(OPレポート・対応履歴)
        </button>
      </div>

      {tab === 'bluebean' ? (
        <BlueBeanUploadCard />
      ) : (

      <div className="card">
        <h3>{tab === 'operators' ? 'オペレーターCSV取込' : '会社CSV取込'}</h3>
        <p className="hint" style={{ marginBottom: 12 }}>
          {tab === 'operators'
            ? '現行シートの「OP管理」から転記したCSVを取り込みます。同一氏名で複数行(BB ID違い)は1人に統合されます。BB ID・内線番号が空欄の行は自動生成します。架電会社は先に会社マスタへ登録しておくと紐付きます。'
            : '現行シートの「会社情報」から転記したCSVを取り込みます。BB業務コードは ; 区切りで複数指定できます。'}
        </p>

        <div className="modal-actions" style={{ marginTop: 0, marginBottom: 14 }}>
          <button
            className="btn-line"
            onClick={() =>
              tab === 'operators'
                ? downloadCsv('operators_template.csv', OPERATOR_TEMPLATE_CSV)
                : downloadCsv('companies_template.csv', COMPANY_TEMPLATE_CSV)
            }
          >
            テンプレートをダウンロード
          </button>
          <label className="btn-green" style={{ marginTop: 0, display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}>
            CSVファイルを選択
            <input type="file" accept=".csv,text/csv" style={{ display: 'none' }} onChange={(e) => void handleFile(e)} disabled={busy} />
          </label>
        </div>

        {done && (
          <div className="badge b-ok" style={{ display: 'block', padding: '10px 14px', fontSize: 13, marginBottom: 12 }}>
            {done}
          </div>
        )}

        {file && plan && (
          <>
            <div className="hint" style={{ marginBottom: 8 }}>
              ファイル: {file.name}(データ{file.rowCount}行)
            </div>

            {plan.errors.length > 0 && (
              <div className="gray-box" style={{ borderColor: 'var(--red)', marginTop: 0 }}>
                <div className="box-title" style={{ color: 'var(--red)' }}>
                  取り込めない行({plan.errors.length}件)— 修正して再度ファイルを選択してください
                </div>
                <ul style={{ paddingLeft: 20, fontSize: 12.5, color: 'var(--red)' }}>
                  {plan.errors.map((e, i) => (
                    <li key={i}>
                      {e.lineNo}行目: {e.reason}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {plan.warnings.length > 0 && (
              <div className="gray-box" style={{ marginTop: 10 }}>
                <div className="box-title">確認事項({plan.warnings.length}件)</div>
                <ul style={{ paddingLeft: 20, fontSize: 12.5, color: 'var(--amber)' }}>
                  {plan.warnings.map((w, i) => (
                    <li key={i}>{w}</li>
                  ))}
                </ul>
              </div>
            )}

            {tab === 'operators' && opPlan && opPlan.creates.length > 0 && (
              <div style={{ marginTop: 14 }}>
                <div className="box-title" style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', marginBottom: 6 }}>
                  新規登録されるオペレーター({opPlan.creates.length}名)
                </div>
                <table>
                  <thead>
                    <tr>
                      <SortTh label="名前" sortKey="name" sort={opSort} onSort={toggleOpSort} defaultDir="asc" />
                      <SortTh label="BB ID(内線)" sortKey="bb_id" sort={opSort} onSort={toggleOpSort} defaultDir="asc" />
                      <SortTh label="架電会社" sortKey="company" sort={opSort} onSort={toggleOpSort} defaultDir="asc" />
                      <SortTh label="架電内容" sortKey="business" sort={opSort} onSort={toggleOpSort} defaultDir="asc" />
                      <SortTh label="在籍" sortKey="status" sort={opSort} onSort={toggleOpSort} defaultDir="asc" />
                      <SortTh label="出勤形態" sortKey="work_style" sort={opSort} onSort={toggleOpSort} defaultDir="asc" />
                      <SortTh label="報酬区分" sortKey="pay_type" sort={opSort} onSort={toggleOpSort} defaultDir="asc" />
                      <SortTh label="時給／単価" sortKey="wage" sort={opSort} onSort={toggleOpSort} className="num" />
                      <SortTh label="入社日" sortKey="joined_at" sort={opSort} onSort={toggleOpSort} />
                    </tr>
                  </thead>
                  <tbody>
                    {opCreates.map((c) => (
                      <tr key={c.name}>
                        <td><b>{c.name}</b></td>
                        <td>
                          <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 4 }}>
                            {c.accounts.map((a, i) => (
                              <span key={i} className={`badge ${a.business === 'kyutoki' ? 'b-plan' : 'b-pend'}`}>
                                {a.bb_id || '(自動)'}({a.ext || '自動'})
                              </span>
                            ))}
                          </span>
                        </td>
                        <td>{Array.from(new Set(c.accounts.map((a) => companyName(a.company_id)))).join('、')}</td>
                        <td>{Array.from(new Set(c.accounts.map((a) => BUSINESS_LABELS[a.business]))).join('／')}</td>
                        <td>{OPERATOR_STATUS_LABELS[c.status]}</td>
                        <td>{WORK_STYLE_LABELS[c.work_style]}</td>
                        <td>{PAY_TYPE_LABELS[c.pay_type]}</td>
                        <td className="num" style={{ whiteSpace: 'nowrap' }}>{c.accounts.map((a) => `${formatYen(a.current_wage)}円`).join('／')}</td>
                        <td>{toDateLabel(c.joined_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {tab === 'companies' && coPlan && coPlan.creates.length > 0 && (
              <div style={{ marginTop: 14 }}>
                <div className="box-title" style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', marginBottom: 6 }}>
                  新規登録される会社({coPlan.creates.length}社)
                </div>
                <table>
                  <thead>
                    <tr>
                      <SortTh label="社名" sortKey="name" sort={coSort} onSort={toggleCoSort} defaultDir="asc" />
                      <SortTh label="会社単価" sortKey="unit_price" sort={coSort} onSort={toggleCoSort} className="num" />
                      <SortTh label="商材" sortKey="product_type" sort={coSort} onSort={toggleCoSort} defaultDir="asc" />
                      <SortTh label="納品タイミング" sortKey="delivery_timing" sort={coSort} onSort={toggleCoSort} defaultDir="asc" />
                      <SortTh label="BB業務コード" sortKey="codes" sort={coSort} onSort={toggleCoSort} defaultDir="asc" />
                      <SortTh label="電話番号" sortKey="phone" sort={coSort} onSort={toggleCoSort} defaultDir="asc" />
                    </tr>
                  </thead>
                  <tbody>
                    {coCreates.map((c) => (
                      <tr key={c.name}>
                        <td><b>{c.name}</b></td>
                        <td className="num">{formatYen(c.unit_price)}円</td>
                        <td>{BUSINESS_LABELS[c.product_type]}</td>
                        <td>{c.delivery_timing || '—'}</td>
                        <td>{c.bb_business_codes.join(', ') || '—'}</td>
                        <td>{c.phone || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="modal-actions">
              <button className="btn-green" disabled={!canRun || busy} onClick={() => void handleImport()}>
                {busy
                  ? '取込中…'
                  : tab === 'operators'
                    ? `取込を実行(新規${createCount}名${mergeCount > 0 ? `・追記${mergeCount}名` : ''})`
                    : `取込を実行(新規${createCount}社)`}
              </button>
              {plan.errors.length > 0 && (
                <span className="hint">エラー行は取り込まれません。実行後にCSVを修正して再取込もできます(登録済みはスキップ)。</span>
              )}
            </div>
          </>
        )}
      </div>
      )}
    </div>
  );
}
